import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import Layout from '../../components/ui/Layout';
import { ProfileNav } from './PersonalDetails';

const DEFAULT_PREFS = {
  failedCheck: { push: true, email: false },
  crashDetection: { push: true, email: true },
  overdueEscalation: { push: true, email: true },
  assetBlocked: { push: true, email: false },
  dailySummary: { push: false, email: true },
  staffInvitationAccepted: { push: true, email: false },
};

const TRIGGER_LABELS = {
  failedCheck: 'Failed check in my branch',
  crashDetection: 'Crash detection alert',
  overdueEscalation: 'Overdue check escalation',
  assetBlocked: 'Asset blocked',
  dailySummary: 'Daily branch summary',
  staffInvitationAccepted: 'Staff invitation accepted',
};

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: value ? 'var(--color-success)' : 'var(--color-danger)',
        border: `0.5px solid ${value ? 'var(--color-success)' : 'var(--color-danger)'}`,
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 0.3s ease, border-color 0.3s ease',
        flexShrink: 0,
      }}
    >
      <div style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: '#fff',
        position: 'absolute',
        top: 2,
        left: value ? 22 : 2,
        transition: 'left 0.3s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

export default function ProfileNotifications() {
  const { user, orgId, userProfile } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const loadPrefs = async () => {
      if (!user?.uid || !resolvedOrgId) return;
      try {
        const snap = await getDoc(
          doc(db, 'organisations', resolvedOrgId, 'users', user.uid)
        );
        if (snap.exists() && snap.data().notificationPrefs) {
          setPrefs(snap.data().notificationPrefs);
        }
      } catch (err) {
        console.error('Error loading prefs:', err);
      }
      setLoading(false);
    };
    loadPrefs();
  }, [user?.uid, resolvedOrgId]);

  const toggle = (trigger, channel) => {
    setPrefs(prev => ({
      ...prev,
      [trigger]: {
        ...prev[trigger],
        [channel]: !prev[trigger][channel],
      },
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateDoc(
        doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
        { notificationPrefs: prefs }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save — ' + err.message);
    }
    setSaving(false);
  };

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', flex: 1, minHeight: '100vh' }}>
        <ProfileNav active="Notifications" />
        <div style={{ flex: 1, padding: 'var(--main-padding)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading preferences...</div>
        </div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ display: 'flex', flex: 1, minHeight: '100vh' }}>
        <ProfileNav active="Notifications" />
        <div style={{ flex: 1, padding: 'var(--main-padding)' }}>
          <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Notifications</h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 28 }}>
            Choose how you want to be notified
          </p>

          <div style={{
            background: 'var(--color-bg-primary)',
            border: '0.5px solid var(--color-border-light)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            marginBottom: 16,
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 100px',
              padding: '10px 20px',
              borderBottom: '0.5px solid var(--color-border-light)',
              background: 'var(--color-bg-secondary)',
            }}>
              {['Trigger', 'Push', 'Email'].map(h => (
                <div key={h} style={{
                  fontSize: 10,
                  fontWeight: 500,
                  color: 'var(--color-text-tertiary)',
                  letterSpacing: '0.04em',
                  textAlign: h !== 'Trigger' ? 'center' : 'left',
                }}>
                  {h}
                </div>
              ))}
            </div>

            {Object.entries(prefs).map(([key, val], i, arr) => (
              <div key={key} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 100px 100px',
                padding: '16px 20px',
                borderBottom: i < arr.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                alignItems: 'center',
              }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                  {TRIGGER_LABELS[key]}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Toggle value={val.push} onChange={() => toggle(key, 'push')} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Toggle value={val.email} onChange={() => toggle(key, 'email')} />
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div style={{
              fontSize: 12,
              color: 'var(--color-danger)',
              background: 'var(--color-danger-light)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 14px',
              marginBottom: 14,
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: saved ? 'var(--color-success)' : 'var(--color-brand)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: '10px 24px',
              fontSize: 13,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              transition: 'background 0.3s',
            }}
          >
            {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save preferences'}
          </button>
        </div>
      </div>
    </Layout>
  );
}